import { useStore } from "zustand";
import type { GetFeedArticlesPagedFromProjectionArticle } from "@/data-access/news-feeds/queries/articles-management-queries";
import { derive } from "@/lib/derive-zustand";
import {
  buildSelectionSnapshot,
  type SelectionSnapshot,
} from "./feed-content-management.helpers";
import useFeedContentManagementStore from "./feed-content-management.slice";

interface ArticleMetrics {
  selection: number | null;
  displayed: number | null;
}

/**
 * Creates a derived selector for percentages based on total articles
 * selection is related to feedVolume
 * displayed is related to pagination.totalCount
 */
const derivePercentages = (totalArticles: number) =>
  derive<ArticleMetrics>((get) => {
    const { feedVolume, pagination } = get(useFeedContentManagementStore);

    const calcPercent = (value: number) =>
      totalArticles ? Math.round((value / totalArticles) * 100) : null;

    return {
      selection: calcPercent(feedVolume ?? 0),
      displayed: calcPercent(pagination.totalCount ?? 0),
    };
  });

/**
 * Hook to get article metrics as percentages of total articles
 */
export const useArticleMetricsAsPercentages = (totalArticles: number) =>
  useStore(derivePercentages(totalArticles));

/**
 * Derived selector that returns projection articles directly from currentPage
 * Uses derive for proper memoization to prevent unnecessary re-renders
 */
const deriveArticles = derive<
  Array<{
    cursor: string;
    node: GetFeedArticlesPagedFromProjectionArticle;
  }>
>((get) => {
  const { currentPage } = get(useFeedContentManagementStore);
  return currentPage.edges;
});

/**
 * Hook to get articles for ArticleCard
 * Returns projection articles directly - no transformation needed
 */
export const useArticles = () => useStore(deriveArticles);

/**
 * Computes a snapshot of the current selection, including support for
 * single-article actions and select-all with exclusions.
 */
const deriveSelectionSnapshot = derive<SelectionSnapshot>((get) =>
  buildSelectionSnapshot(get(useFeedContentManagementStore)),
);

/**
 * Hook to get the selection snapshot inside React components.
 */
export const useSelectionSnapshot = () => useStore(deriveSelectionSnapshot);

/**
 * Derived selector for displaying the number of selected articles,
 * ignoring any single-article overrides.
 */
const deriveSelectedCount = derive<number>((get) => {
  const { isAllSelected, selectedArticleIds, excludedArticleIds, pagination } =
    get(useFeedContentManagementStore);

  if (isAllSelected) {
    const totalCount = pagination.totalCount ?? 0;
    return Math.max(totalCount - excludedArticleIds.size, 0);
  }

  return selectedArticleIds.size;
});

/**
 * Hook to get the number of selected articles for display purposes
 */
export const useDisplaySelectedNumberOfArticles = () =>
  useStore(deriveSelectedCount);
